import React, { useRef, useState } from 'react'
import { Route, RouteChildrenProps, Switch } from 'react-router-dom'
import {
  routes,
  useGetUnreadNotificationsCount,
  WorkspaceSidebarLayoutExtraProps,
} from '@sketch/modules-common'
import { useLocalStorage, useResizableSidePanel } from '@sketch/utils'

import {
  EmptyPreview,
  EmptyPreviewIcon,
  EmptyPreviewText,
  HeaderActions,
  ListHeaderWrapper,
  Wrapper,
  ListAndHeaderWrapper,
  ListHeaderText,
  UpdatePreview,
  InboxResizeHandle,
} from './Inbox.styles'
import { InboxList } from './InboxList'
import { InboxPreview } from './InboxPreview'
import { Filter, Pluralize } from '@sketch/components'
import {
  InboxSearchProvider,
  useInboxSearchContext,
} from './InboxSearchContext'
import { InboxFilterMenu } from './InboxFilterMenu'
import { InboxSortMenu } from './InboxSortMenu'

const INBOX_LIST_WIDTH_KEY = 'inbox-list-width'
const INBOX_LIST_DEFAULT_WIDTH = 450
const INBOX_LIST_MIN_WIDTH = 320
const INBOX_LIST_MAX_WIDTH = 720

export type InboxViewProps = RouteChildrenProps &
  WorkspaceSidebarLayoutExtraProps

const InboxContent = (props: InboxViewProps) => {
  const { useOverrideLayoutProps } = props
  const { search, setSearch } = useInboxSearchContext()
  const [isSearchExpanded, setIsSearchExpanded] = useState(false)
  const unreadCount = useGetUnreadNotificationsCount()

  const containerRef = useRef<HTMLDivElement | null>(null)
  const [storedWidth, setStoredWidth] = useLocalStorage<number>(
    INBOX_LIST_WIDTH_KEY,
    INBOX_LIST_DEFAULT_WIDTH
  )
  const { width, resizeHandleRef } = useResizableSidePanel({
    initialWidth: storedWidth,
    defaultWidth: INBOX_LIST_DEFAULT_WIDTH,
    minWidth: INBOX_LIST_MIN_WIDTH,
    maxWidth: INBOX_LIST_MAX_WIDTH,
    // The list panel is inset within the workspace layout (workspace sidebar
    // sits to its left), so we anchor the drag math to the panel's own left
    // edge rather than the viewport's.
    getOffsetLeft: () =>
      containerRef.current?.getBoundingClientRect().left ?? 0,
    // Don't trample the workspace sidebar's `--sidebar-width` variable.
    cssVariable: null,
    onWidthUpdate: setStoredWidth,
  })

  useOverrideLayoutProps({
    title: 'Inbox',
    hasNavbar: false,
    hideFooter: true,
    hideHeader: true,
    hideMargin: true,
  })

  return (
    <Wrapper>
      <ListAndHeaderWrapper ref={containerRef} style={{ width }}>
        <ListHeaderWrapper>
          <ListHeaderText>Inbox</ListHeaderText>

          <HeaderActions $hidden={isSearchExpanded}>
            <InboxFilterMenu />
            <InboxSortMenu />
          </HeaderActions>

          <Filter
            collapsible
            fullWidth={isSearchExpanded}
            value={search}
            onChange={setSearch}
            onClear={() => setSearch('')}
            onExpandedChange={setIsSearchExpanded}
          />
        </ListHeaderWrapper>

        <InboxList />
        <InboxResizeHandle ref={resizeHandleRef} />
      </ListAndHeaderWrapper>

      <UpdatePreview>
        <Switch>
          <Route
            path={routes.UPDATES_ITEM.template()}
            render={({ match }) => {
              const notificationId = (
                match.params as { notificationId?: string }
              ).notificationId
              if (!notificationId) return null

              return <InboxPreview notificationId={notificationId} />
            }}
          />

          <Route
            render={() => (
              <EmptyPreview>
                <EmptyPreviewIcon />
                <Pluralize
                  singular="Unread Notification"
                  count={unreadCount}
                  showCount
                  wrapperElement={EmptyPreviewText}
                />
              </EmptyPreview>
            )}
          />
        </Switch>
      </UpdatePreview>
    </Wrapper>
  )
}

export const Inbox = (props: InboxViewProps) => (
  <InboxSearchProvider>
    <InboxContent {...props} />
  </InboxSearchProvider>
)
